import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { FileEdit, FileText, ChevronRight, Plus, Minus, Loader2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * FileChangeCard — Emergent-parity card for a single staged file.
 *
 * Renders:
 *   ┌─ 📝 backend/routes/foo.py         [modified] +42 −8 ─┐
 *   │  Preview │ Diff ▶                                      │
 *   │  ┌──────────────────────────────────────────────────┐ │
 *   │  │  <colorized unified diff, lazy-loaded on click>  │ │
 *   │  └──────────────────────────────────────────────────┘ │
 *   └────────────────────────────────────────────────────────┘
 *
 * Diff fetched lazily via POST /api/dev-console/phase2/diff-preview when the
 * user clicks the expander — never eagerly, to keep the chat lean.
 */
export function FileChangeCard({ filePath, sessionId, kind = 'modified' }) {
  const [expanded, setExpanded] = useState(false);
  const [diff, setDiff] = useState(null);   // { unified_diff, ins, del, kind }
  const [loading, setLoading] = useState(false);

  const fetchDiff = useCallback(async () => {
    if (diff || loading) return;
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/api/dev-console/phase2/diff-preview`,
        { session_id: sessionId },
      );
      const mine = (data.diffs || []).find((d) => d.path === filePath);
      if (!mine) {
        toast.error(`No diff for ${filePath} — file may have been committed`);
        setDiff({ unified_diff: '(no diff available)', ins: 0, del: 0, kind });
      } else {
        setDiff(mine);
      }
    } catch (e) {
      toast.error('Diff fetch failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setLoading(false);
    }
  }, [filePath, sessionId, diff, loading, kind]);

  const onToggle = useCallback(() => {
    const next = !expanded;
    setExpanded(next);
    if (next) fetchDiff();
  }, [expanded, fetchDiff]);

  const Icon = kind === 'created' ? FileText : FileEdit;
  const kindLabel = kind === 'created' ? 'new' : kind === 'deleted' ? 'deleted' : 'modified';
  const kindColor = {
    created: 'text-emerald-400 bg-emerald-950/40 border-emerald-800',
    modified: 'text-amber-300 bg-amber-950/40 border-amber-800',
    deleted: 'text-red-300 bg-red-950/40 border-red-800',
  }[kind] || 'text-zinc-400 bg-zinc-800/40 border-zinc-700';

  return (
    <div
      data-testid={`file-change-card-${filePath.replace(/[^a-z0-9]/gi, '-')}`}
      className="rounded-lg border border-zinc-800 bg-zinc-900/50 overflow-hidden text-xs"
    >
      <button
        type="button"
        onClick={onToggle}
        className="w-full flex items-center gap-2 px-2.5 py-1.5 hover:bg-zinc-800/60 transition-colors text-left"
      >
        <Icon className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
        <span className="font-mono text-zinc-200 truncate flex-1">{filePath}</span>
        <span className={`text-[9px] uppercase px-1.5 py-0.5 rounded border ${kindColor}`}>{kindLabel}</span>
        {diff ? (
          <span className="font-mono text-[10px] flex items-center gap-1.5">
            <span className="text-emerald-400 flex items-center gap-0.5">
              <Plus className="h-2.5 w-2.5" />{diff.ins || 0}
            </span>
            <span className="text-red-400 flex items-center gap-0.5">
              <Minus className="h-2.5 w-2.5" />{diff.del || 0}
            </span>
          </span>
        ) : null}
        <ChevronRight
          className={`h-3.5 w-3.5 text-zinc-500 shrink-0 transition-transform ${expanded ? 'rotate-90' : ''}`}
        />
      </button>
      {expanded ? (
        <div className="border-t border-zinc-800 bg-zinc-950 max-h-[280px] overflow-auto">
          {loading ? (
            <div className="p-3 flex items-center gap-2 text-zinc-500 text-[11px]">
              <Loader2 className="h-3 w-3 animate-spin" /> Loading diff…
            </div>
          ) : diff ? (
            <pre className="p-2 font-mono text-[10px] leading-[1.35] whitespace-pre-wrap">
              {(diff.unified_diff || '').split('\n').map((line, i) => {
                let cls = 'text-zinc-400';
                if (line.startsWith('+') && !line.startsWith('+++')) cls = 'text-emerald-300 bg-emerald-950/20';
                else if (line.startsWith('-') && !line.startsWith('---')) cls = 'text-red-300 bg-red-950/20';
                else if (line.startsWith('@@')) cls = 'text-sky-400 bg-sky-950/20';
                return (
                  <div key={i} className={cls}>{line || ' '}</div>
                );
              })}
            </pre>
          ) : (
            <div className="p-3 text-zinc-500 text-[11px]">No diff data</div>
          )}
        </div>
      ) : null}
    </div>
  );
}
